// Cambios clave: uso de queryParams, un solo useEffect, y funciones actualizadoras
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  Fragment,
} from "react";
import Swal from "sweetalert2";
import {
  Grid,
  TextField,
  Autocomplete,
  CircularProgress,
  LinearProgress,
  Fade,
} from "@mui/material";
import ReactPaginate from "react-paginate";
import Table from "../../Components/Common/Table/Table";
import {
  getInfoMessageNoResultsSearch,
  CommonSubHeader,
} from "../../Components/helpers/Helpers";
import TableActions from "../../Components/Common/Table/TableActions";
import { Groups3 } from "@mui/icons-material";
import api from "../../api/axios";
import LoadingPage from "../../Components/Common/Loading/LoadingPage";
import { ChevronLeft, ChevronRight } from "lucide-react";

function Associations() {
  const [data, setData] = useState({ associations: [] });
  const [changeDone, setChangeDone] = useState(false);

  const [dataTable, setDataTable] = useState({
    totalPagesNumber: 0,
    pageCount: 0,
  });
  const [flag, setFlag] = useState(false);
  const [options, setOptions] = useState({
    associations: [],
    provinces: [],
    groups: [],
    companies: [],
  });
  const [loading, setLoading] = useState({
    associations: false,
    provinces: false,
    groups: false,
    companies: false,
  });
  const [query, setQuery] = useState({ phone: "", email: "" });

  //   Un solo estado unificado para filtros y paginación
  const [queryParams, setQueryParams] = useState({
    filters: {
      associationId: 0,
      groupId: 0,
      companyId: 0,
      provinceFilter: 0,
      phoneFilter: "",
      emailFilter: "",
    },
    pagination: {
      objPerPage: 10,
      pagePressed: 1,
    },
  });

  const getDataAssociations = async () => {
    setFlag(true);

    const params = {
      page: queryParams.pagination.pagePressed,
      objPerPage: queryParams.pagination.objPerPage,
      company_id: queryParams.filters.companyId,
      asoc_id: queryParams.filters.associationId,
      asoc_grupo: queryParams.filters.groupId,
      province_name: queryParams.filters.provinceFilter,
      phone: queryParams.filters.phoneFilter,
      email: queryParams.filters.emailFilter,
    };

    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/associations`,
        params,
      );

      if (response?.data?.associations) {
        const { associations, groupsList, provincesList, companyList } =
          response.data;
        setData({ associations: associations.data });
        setDataTable({
          totalPagesNumber: associations.total,
          pageCount: associations.last_page,
        });
        setOptions({
          associations: [
            { asoc_name: "Seleccionar", id: 0 },
            ...response.data.associationsList,
          ],
          groups: [{ grupo: "Seleccionar", id: 0 }, ...groupsList],
          provinces: [
            { provincia: "Seleccionar", id_provincia: 0 },
            ...provincesList,
          ],
          companies: [{ company_name: "Seleccionar", id: 0 }, ...companyList],
        });

        if (associations.total === 0) {
          getInfoMessageNoResultsSearch(
            "¡No se encontraron resultados!",
            "Por favor cambia los filtros o realiza una nueva búsqueda",
            "error",
            true,
            "Continuar",
            false,
          );
        } else {
          Swal.close();
        }
      }
    } catch (error) {
      console.error("Error en la respuesta del servidor:", error);
    } finally {
      setFlag(false);
      setLoading((prev) => ({ ...prev, companies: false }));
    }
  };

  //   Un solo efecto para filtros + paginación
  useEffect(() => {
    console.log("useEffect disparado");
    getDataAssociations();
  }, [JSON.stringify(queryParams), changeDone]);

  //   Actualización con debounce
  const updateFilter = useCallback((field, value) => {
    setQueryParams((prev) => ({
      ...prev,
      filters: { ...prev.filters, [field]: value },
    }));
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(
      () => updateFilter("phoneFilter", query.phone),
      1500,
    );
    return () => clearTimeout(timeoutId);
  }, [query.phone, updateFilter]);

  useEffect(() => {
    const timeoutId = setTimeout(
      () => updateFilter("emailFilter", query.email),
      1500,
    );
    return () => clearTimeout(timeoutId);
  }, [query.email, updateFilter]);

  const handlePageClick = (event) => {
    setQueryParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, pagePressed: event.selected + 1 },
    }));
  };

  const handleAutocompleteOpen = useCallback((field) => {
    setLoading((prev) => ({ ...prev, [field]: true }));
  }, []);

  const renderCellWithDefault = (value, defaultValue = "Sin asignar") => (
    <div className="spanClassForTables centerContent">
      {value || defaultValue}
    </div>
  );

  const renderAgreement = (value) => (
    <div className="spanClassForTables centerContent">
      {value === 1 ? "Acu. Directo" : "NO"}
    </div>
  );

  const columns = useMemo(
    () => [
      {
        Header: `LISTA DE ASOCIACIONES`,
        columns: [
          {
            Header: "Asociación",
            accessor: "asoc_name",
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Grupo",
            accessor: "group_relations.grupo",
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Acuerdo",
            accessor: "asoc_acuerdo",
            Cell: ({ value }) => renderAgreement(value),
          },
          {
            Header: "Provincia",
            accessor: "asoc_province",
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Teléfono",
            accessor: "asoc_phone",
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Email",
            accessor: "asoc_email",
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Acciones",
            accessor: "id",
            Cell: ({ value }) => (
              <TableActions
                rute={"asociaciones"}
                value={value}
                showEdit
                showView
                showDelete
                module={"association"}
                apiRute={"associations"}
                setChangeDone={setChangeDone}
                changeDone={changeDone}
              />
            ),
          },
        ],
      },
    ],
    [],
  );

  const filterFields = [
    {
      type: "autocomplete",
      label: "Asociación",
      stateKey: "associationId",
      optionKey: "associations",
      optionLabel: "asoc_name",
    },
    {
      type: "autocomplete",
      label: "Grupo",
      stateKey: "groupId",
      optionKey: "groups",
      optionLabel: "grupo",
    },
    {
      type: "autocomplete",
      label: "Empresa",
      stateKey: "companyId",
      optionKey: "companies",
      optionLabel: "company_name",
    },
    {
      type: "autocomplete",
      label: "Provincia",
      stateKey: "provinceFilter",
      optionKey: "provinces",
      optionLabel: "provincia",
    },
    {
      type: "textField",
      label: "Teléfono",
      stateKey: "phone",
    },
    {
      type: "textField",
      label: "Email",
      stateKey: "email",
    },
  ];

  useEffect(() => {
    if (
      dataTable.pageCount > 0 &&
      queryParams.pagination.pagePressed > dataTable.pageCount
    ) {
      setQueryParams((prev) => ({
        ...prev,
        pagination: { ...prev.pagination, pagePressed: 1 },
      }));
    }
  }, [dataTable.pageCount, queryParams.pagination.pagePressed]);

  const content = (
    <div>
      <Grid
        container
        direction="row"
        m={"10px 0"}
        item
        xs={12}
        className="filtersContainer">
        {filterFields.map((field, index) => (
          <Grid key={index} item xs={4} md={2} sx={{ padding: "5px" }}>
            {field.type === "autocomplete" ? (
              <Autocomplete
                size="small"
                value={
                  options[field.optionKey].find((opt) => {
                    const val = queryParams.filters[field.stateKey];
                    if (field.stateKey === "provinceFilter")
                      return opt.provincia === val;
                    return opt.id === val;
                  }) || null
                }
                onChange={(event, value) =>
                  setQueryParams((prev) => ({
                    ...prev,
                    filters: {
                      ...prev.filters,
                      [field.stateKey]: value
                        ? value.provincia
                          ? value.provincia
                          : value.id
                        : 0,
                    },
                  }))
                }
                open={loading[field.optionKey]}
                onOpen={() => handleAutocompleteOpen(field.optionKey)}
                onClose={() =>
                  setLoading((prev) => ({
                    ...prev,
                    [field.optionKey]: false,
                  }))
                }
                getOptionLabel={(option) =>
                  option[field.optionLabel] || "Seleccionar"
                }
                isOptionEqualToValue={(option, value) =>
                  option.id === value.id || option.provincia === value.provincia
                }
                options={options[field.optionKey]}
                loading={loading[field.optionKey]}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={field.label}
                    autoComplete="new-password"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <Fragment>
                          {loading[field.optionKey] && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </Fragment>
                      ),
                    }}
                  />
                )}
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                label={field.label}
                autoComplete="new-password"
                value={query[field.stateKey] || ""}
                onChange={(e) =>
                  setQuery((prev) => ({
                    ...prev,
                    [field.stateKey]: e.target.value,
                  }))
                }
              />
            )}
          </Grid>
        ))}
      </Grid>

      {flag && <LoadingPage isLoading={flag} heightProp={"80vh"} />}

      {!flag && (
        <Fade in={!flag} timeout={800}>
          <div>
            <Table
              columns={columns}
              data={data.associations}
              view="asociacion"
              viewTitle="asociaciones"
              totalPagesNumber={dataTable.totalPagesNumber}
              tableWidth="unset"
              minTableWidth="80%"
              icon={Groups3}
              viewPermission={"association"}
            />
          </div>
        </Fade>
      )}

      {dataTable.totalPagesNumber > 10 && (
        <div className="tablePaginateContainer">
          <div className="paginatorLeftText">
            Mostrando{" "}
            {queryParams.pagination.objPerPage *
              (queryParams.pagination.pagePressed - 1) +
              1}{" "}
            a{" "}
            {Math.min(
              queryParams.pagination.objPerPage *
                queryParams.pagination.pagePressed,
              dataTable.totalPagesNumber,
            )}{" "}
            de {dataTable.totalPagesNumber} resultados
          </div>

          <ReactPaginate
            breakLabel="..."
            nextLabel={<ChevronRight size={18} />}
            previousLabel={<ChevronLeft size={18} />}
            onPageChange={handlePageClick}
            pageRangeDisplayed={3}
            marginPagesDisplayed={2}
            pageCount={dataTable.pageCount}
            containerClassName="pagination"
            activeClassName="active"
            forcePage={queryParams.pagination.pagePressed - 1}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Configuración" subTitle="Asociaciones" />

      <div className="mainContainer">
        <div className="subMainContainer">{content}</div>
      </div>
    </div>
  );
}

export default Associations;
